import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

// ---------- constantes ----------
const TIPOS_ITEM = [
  { valor: 'bool',     label: 'Sim / Não (checkbox)' },
  { valor: 'opcoes',   label: 'Múltipla escolha' },
  { valor: 'texto',    label: 'Texto livre' },
  { valor: 'numerico', label: 'Número' },
  { valor: 'faixa',    label: 'Faixa (de / até)' },
];

// ---------- sub-componentes ----------

function BadgeTipo({ tipo }) {
  const map = {
    bool:     'badge-ok',
    opcoes:   'badge-done',
    texto:    'badge-pending',
    numerico: 'badge-pending',
    faixa:    'badge-pending',
  };
  const labels = {
    bool:     'Sim/Não',
    opcoes:   'Múltipla escolha',
    texto:    'Texto',
    numerico: 'Número',
    faixa:    'Faixa',
  };
  return <span className={map[tipo] || 'badge-pending'}>{labels[tipo] || tipo}</span>;
}

function FormularioNovaOpcao({ opcoes, onChange }) {
  function adicionarOpcao() {
    onChange([...opcoes, { valor: '', label: '' }]);
  }
  function removerOpcao(idx) {
    onChange(opcoes.filter((_, i) => i !== idx));
  }
  function atualizarOpcao(idx, campo, val) {
    onChange(opcoes.map((o, i) => i === idx ? { ...o, [campo]: val } : o));
  }

  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs font-semibold text-gray-600">Opções disponíveis</p>
      {opcoes.map((op, idx) => (
        <div key={idx} className="flex gap-2 items-center">
          <input
            type="text"
            value={op.valor}
            onChange={e => atualizarOpcao(idx, 'valor', e.target.value)}
            className="input text-xs py-1.5"
            placeholder="valor (ex: sim)"
          />
          <input
            type="text"
            value={op.label}
            onChange={e => atualizarOpcao(idx, 'label', e.target.value)}
            className="input text-xs py-1.5"
            placeholder="rótulo (ex: Sim)"
          />
          <button
            type="button"
            onClick={() => removerOpcao(idx)}
            className="btn-danger px-2 py-1.5 shrink-0"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={adicionarOpcao}
        className="text-xs text-brand-600 hover:text-brand-800 font-medium flex items-center gap-1"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Adicionar opção
      </button>
    </div>
  );
}

function FormularioItem({ moduloId, itemEditando, onSalvar, onCancelar }) {
  const [label, setLabel] = useState(itemEditando?.label || '');
  const [descricao, setDescricao] = useState(itemEditando?.descricao || '');
  const [tipo, setTipo] = useState(itemEditando?.tipo || 'bool');
  const [opcoes, setOpcoes] = useState(itemEditando?.opcoes || []);
  const [unidade, setUnidade] = useState(itemEditando?.unidade || '');
  const [recomendacao, setRecomendacao] = useState(itemEditando?.recomendacao || '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  async function handleSalvar(e) {
    e.preventDefault();
    if (!label.trim()) { setErro('Informe o rótulo do item.'); return; }
    if (tipo === 'opcoes' && opcoes.length < 2) {
      setErro('Adicione pelo menos 2 opções para múltipla escolha.');
      return;
    }
    setErro('');
    setSalvando(true);
    try {
      const payload = {
        label: label.trim(),
        descricao: descricao.trim(),
        tipo,
        opcoes: tipo === 'opcoes' ? opcoes : [],
        unidade: (tipo === 'numerico' || tipo === 'faixa') ? unidade.trim() : undefined,
        recomendacao: recomendacao.trim() || undefined,
      };
      if (itemEditando) {
        await api.put(`/modulos/${moduloId}/itens/${itemEditando.id}`, payload);
      } else {
        await api.post(`/modulos/${moduloId}/itens`, payload);
      }
      onSalvar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar item.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={handleSalvar} className="bg-white border border-brand-200 rounded-xl p-4 mt-3 space-y-3">
      <p className="text-xs font-semibold text-brand-700 uppercase tracking-wide">
        {itemEditando ? 'Editar item' : 'Novo item'}
      </p>

      <div>
        <label className="label text-xs">Rótulo (label) *</label>
        <input
          type="text"
          value={label}
          onChange={e => setLabel(e.target.value)}
          className="input text-sm"
          placeholder="Ex: Utiliza nota fiscal eletrônica?"
          autoFocus
        />
      </div>

      <div>
        <label className="label text-xs">
          Descrição / tooltip
          <span className="ml-1 text-gray-400 font-normal">(exibida ao cliente como ajuda)</span>
        </label>
        <textarea
          value={descricao}
          onChange={e => setDescricao(e.target.value)}
          className="input text-sm resize-none"
          rows={2}
          placeholder="Explique o que este campo significa para o cliente poder entender e responder com segurança..."
        />
      </div>

      <div>
        <label className="label text-xs">
          Recomendação
          <span className="ml-1 text-gray-400 font-normal">(sugestão exibida ao cliente — opcional)</span>
        </label>
        <input
          type="text"
          value={recomendacao}
          onChange={e => setRecomendacao(e.target.value)}
          className="input text-sm"
          placeholder="Ex: Recomendamos habilitar esta opção para controle de estoque mínimo"
        />
      </div>

      <div>
        <label className="label text-xs">Tipo de campo</label>
        <select
          value={tipo}
          onChange={e => setTipo(e.target.value)}
          className="input text-sm"
        >
          {TIPOS_ITEM.map(t => (
            <option key={t.valor} value={t.valor}>{t.label}</option>
          ))}
        </select>
      </div>

      {(tipo === 'numerico' || tipo === 'faixa') && (
        <div>
          <label className="label text-xs">Unidade <span className="text-gray-400 font-normal">(ex: dias, %, R$, horas)</span></label>
          <input
            type="text"
            value={unidade}
            onChange={e => setUnidade(e.target.value)}
            className="input text-sm w-36"
            placeholder="ex: dias"
          />
        </div>
      )}

      {tipo === 'opcoes' && (
        <FormularioNovaOpcao opcoes={opcoes} onChange={setOpcoes} />
      )}

      {erro && <p className="text-xs text-red-600">{erro}</p>}

      <div className="flex gap-2 justify-end pt-1">
        <button type="button" onClick={onCancelar} className="btn-ghost text-xs">
          Cancelar
        </button>
        <button type="submit" disabled={salvando} className="btn-primary text-xs">
          {salvando ? 'Salvando...' : 'Salvar item'}
        </button>
      </div>
    </form>
  );
}

function AccordionModulo({ modulo, onAtualizar }) {
  const [aberto, setAberto] = useState(false);
  const [editandoNome, setEditandoNome] = useState(false);
  const [novoNome, setNovoNome] = useState(modulo.nome);
  const [formularioAberto, setFormularioAberto] = useState(false);
  const [itemEditando, setItemEditando] = useState(null);
  const [confirmandoExcluirModulo, setConfirmandoExcluirModulo] = useState(false);
  const [confirmandoExcluirItem, setConfirmandoExcluirItem] = useState(null);
  const [salvandoNome, setSalvandoNome] = useState(false);
  const [salvandoAtivo, setSalvandoAtivo] = useState(false);

  // Itens vêm direto das props — onAtualizar() refaz GET /modulos que traz tudo
  const itens = modulo.itens || [];

  function abrirAccordion() {
    setAberto(prev => !prev);
  }

  async function salvarNome() {
    if (!novoNome.trim() || novoNome === modulo.nome) {
      setEditandoNome(false);
      return;
    }
    setSalvandoNome(true);
    try {
      await api.put(`/modulos/${modulo.id}`, { nome: novoNome.trim() });
      onAtualizar();
    } catch {
      setNovoNome(modulo.nome);
    } finally {
      setSalvandoNome(false);
      setEditandoNome(false);
    }
  }

  async function toggleAtivo() {
    setSalvandoAtivo(true);
    try {
      await api.put(`/modulos/${modulo.id}`, { ativo: !modulo.ativo });
      onAtualizar();
    } finally {
      setSalvandoAtivo(false);
    }
  }

  async function excluirModulo() {
    try {
      await api.delete(`/modulos/${modulo.id}`);
      onAtualizar();
    } catch {
      setConfirmandoExcluirModulo(false);
    }
  }

  async function excluirItem(itemId) {
    try {
      await api.delete(`/modulos/${modulo.id}/itens/${itemId}`);
      setConfirmandoExcluirItem(null);
      onAtualizar();
    } catch {
      setConfirmandoExcluirItem(null);
    }
  }

  function aposFormularioSalvo() {
    setFormularioAberto(false);
    setItemEditando(null);
    onAtualizar();
  }

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden mb-3">
      {/* Cabeçalho do módulo */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white">
        <button
          type="button"
          onClick={abrirAccordion}
          className="flex items-center gap-2 flex-1 text-left"
        >
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${aberto ? 'rotate-90' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>

          {editandoNome ? (
            <input
              type="text"
              value={novoNome}
              onChange={e => setNovoNome(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') salvarNome(); if (e.key === 'Escape') { setEditandoNome(false); setNovoNome(modulo.nome); } }}
              onClick={e => e.stopPropagation()}
              className="input text-sm py-1 max-w-xs"
              autoFocus
            />
          ) : (
            <span className="font-semibold text-gray-800">{modulo.nome}</span>
          )}
        </button>

        <div className="flex items-center gap-2 shrink-0">
          {/* Toggle ativo */}
          <button
            type="button"
            onClick={toggleAtivo}
            disabled={salvandoAtivo}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              modulo.ativo ? 'bg-brand-600' : 'bg-gray-200'
            }`}
            title={modulo.ativo ? 'Inativar módulo' : 'Ativar módulo'}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                modulo.ativo ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
          <span className="text-xs text-gray-400 w-14">
            {modulo.ativo ? 'Ativo' : 'Inativo'}
          </span>

          {editandoNome ? (
            <button
              type="button"
              onClick={salvarNome}
              disabled={salvandoNome}
              className="btn-primary text-xs px-3 py-1"
            >
              {salvandoNome ? '...' : 'Salvar'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setEditandoNome(true)}
              className="btn-ghost text-xs px-2 py-1"
            >
              Renomear
            </button>
          )}

          {confirmandoExcluirModulo ? (
            <div className="flex gap-1">
              <button onClick={excluirModulo} className="btn-danger text-xs px-2 py-1">
                Confirmar
              </button>
              <button onClick={() => setConfirmandoExcluirModulo(false)} className="btn-ghost text-xs px-2 py-1">
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmandoExcluirModulo(true)}
              className="btn-danger text-xs px-2 py-1"
            >
              Excluir
            </button>
          )}
        </div>
      </div>

      {/* Conteúdo expansível */}
      {aberto && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-4">
          {itens.length === 0 && !formularioAberto ? (
            <p className="text-sm text-gray-400 mb-3">Nenhum item cadastrado ainda.</p>
          ) : (
            <div className="space-y-2 mb-3">
              {itens.map(item => (
                <div key={item.id} className="bg-white border border-gray-200 rounded-lg px-3 py-2.5">
                  {itemEditando?.id === item.id ? (
                    <FormularioItem
                      moduloId={modulo.id}
                      itemEditando={item}
                      onSalvar={aposFormularioSalvo}
                      onCancelar={() => setItemEditando(null)}
                    />
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-start gap-2 min-w-0">
                        <BadgeTipo tipo={item.tipo} />
                        <div className="min-w-0">
                          <span className="text-sm text-gray-800">{item.label}</span>
                          {item.tipo === 'opcoes' && item.opcoes?.length > 0 && (
                            <span className="text-xs text-gray-400 ml-1.5">
                              ({item.opcoes.length} opções)
                            </span>
                          )}
                          {item.unidade && (
                            <span className="text-xs text-gray-400 ml-1.5">
                              em {item.unidade}
                            </span>
                          )}
                          {item.descricao && (
                            <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{item.descricao}</p>
                          )}
                          {item.recomendacao && (
                            <p className="text-xs text-amber-600 mt-0.5 leading-relaxed">
                              <span className="font-semibold">Recomendação:</span> {item.recomendacao}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => { setItemEditando(item); setFormularioAberto(false); }}
                          className="btn-ghost text-xs px-2 py-1"
                        >
                          Editar
                        </button>
                        {confirmandoExcluirItem === item.id ? (
                          <div className="flex gap-1">
                            <button
                              onClick={() => excluirItem(item.id)}
                              className="btn-danger text-xs px-2 py-1"
                            >
                              Confirmar
                            </button>
                            <button
                              onClick={() => setConfirmandoExcluirItem(null)}
                              className="btn-ghost text-xs px-2 py-1"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmandoExcluirItem(item.id)}
                            className="btn-danger text-xs px-2 py-1"
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Formulário novo item */}
          {formularioAberto && !itemEditando ? (
            <FormularioItem
              moduloId={modulo.id}
              itemEditando={null}
              onSalvar={aposFormularioSalvo}
              onCancelar={() => setFormularioAberto(false)}
            />
          ) : (
            !itemEditando && (
              <button
                type="button"
                onClick={() => setFormularioAberto(true)}
                className="btn-secondary text-xs w-full justify-center mt-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Novo Item
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ---------- página principal ----------

export default function GerenciarModulos() {
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [novoNomeModulo, setNovoNomeModulo] = useState('');
  const [criandoModulo, setCriandoModulo] = useState(false);
  const [formularioCriarAberto, setFormularioCriarAberto] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    carregarModulos();
  }, []);

  async function carregarModulos() {
    setLoading(true);
    try {
      const { data } = await api.get('/modulos');
      setModulos(data);
    } catch {
      setErro('Erro ao carregar módulos.');
    } finally {
      setLoading(false);
    }
  }

  async function criarModulo(e) {
    e.preventDefault();
    if (!novoNomeModulo.trim()) return;
    setCriandoModulo(true);
    try {
      await api.post('/modulos', { nome: novoNomeModulo.trim() });
      setNovoNomeModulo('');
      setFormularioCriarAberto(false);
      await carregarModulos();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao criar módulo.');
    } finally {
      setCriandoModulo(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <img src="/logo_ht.png" alt="HeadThink" className="h-9 w-auto" />
            <div className="h-6 w-px bg-gray-200" />
            <div>
              <p className="text-xs text-gray-400 leading-none">Painel Administrativo</p>
              <p className="text-sm font-semibold text-gray-800 leading-tight">Gerenciar Módulos</p>
            </div>
          </div>
          <Link to="/admin" className="btn-ghost text-xs">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Voltar ao Dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="section-title">Módulos e Itens</h1>
            <p className="section-sub mb-0">
              Configure os módulos disponíveis e seus campos de configuração.
            </p>
          </div>
          <button
            onClick={() => setFormularioCriarAberto(prev => !prev)}
            className="btn-primary text-xs"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo Módulo
          </button>
        </div>

        {/* Formulário novo módulo */}
        {formularioCriarAberto && (
          <div className="card mb-6">
            <p className="text-sm font-semibold text-gray-800 mb-3">Criar novo módulo</p>
            <form onSubmit={criarModulo} className="flex gap-2">
              <input
                type="text"
                value={novoNomeModulo}
                onChange={e => setNovoNomeModulo(e.target.value)}
                className="input"
                placeholder="Nome do módulo (ex: Fiscal, Financeiro...)"
                autoFocus
              />
              <button type="submit" disabled={criandoModulo} className="btn-primary whitespace-nowrap">
                {criandoModulo ? 'Criando...' : 'Criar'}
              </button>
              <button
                type="button"
                onClick={() => { setFormularioCriarAberto(false); setNovoNomeModulo(''); }}
                className="btn-ghost"
              >
                Cancelar
              </button>
            </form>
          </div>
        )}

        {erro && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{erro}</p>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-14 bg-white border border-gray-200 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : modulos.length === 0 ? (
          <div className="card text-center py-16">
            <div className="text-gray-300 text-5xl mb-4">⚙️</div>
            <p className="font-medium text-gray-500">Nenhum módulo cadastrado</p>
            <p className="text-sm text-gray-400 mt-1">Clique em "Novo Módulo" para começar.</p>
          </div>
        ) : (
          <div>
            {modulos.map(modulo => (
              <AccordionModulo
                key={modulo.id}
                modulo={modulo}
                onAtualizar={carregarModulos}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
